// §5.4 — Snap Assist. "After a window fills one zone, the remaining zones show a chooser of the
// other open windows; picking one snaps it."
//
// **Where this renders.** §6.1's component list is closed and Snap Assist is not on it, so it has
// no slot of its own. It hangs off the Windows skin's `Bar`, which is the one surface this skin
// owns that is always mounted and sits *above* the window layer (`.bar-layer`, z 4). The panel is
// `position: fixed`, so it covers the offered zone rather than the taskbar (D-108).
//
// It is **transient**: it appears on the snap that created an empty zone and goes away on Escape,
// on a click anywhere else, or when there is nothing left to offer. A derived-forever chooser
// would sit on top of half the desktop until somebody filled it.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Rect, SnapLayout, Win } from '@hangar/shared';
import { COPY } from '@/copy';
import { zoneRect } from '@/kernel/wm/geometry';
import { useActiveDesktopId, useKernel, useWindows } from '@/kernel/react';

interface Offer {
  groupId: string;
  layoutId: string;
  zoneId: string;
  rect: Rect;
}

/** The zones of `layout` that no window in `groupId` occupies, in the layout's own order. */
export function emptyZones(
  layout: SnapLayout,
  windows: readonly Win[],
  groupId: string,
): SnapLayout['zones'] {
  const filled = new Set(
    windows.filter((win) => win.snap?.groupId === groupId).map((win) => win.snap?.zoneId),
  );
  return layout.zones.filter((zone) => !filled.has(zone.id));
}

/** Windows that could go in the empty zone: on this desktop, resizable, and not already in it. */
export function assistCandidates(
  windows: readonly Win[],
  desktopId: string,
  groupId: string,
): Win[] {
  return windows
    .filter(
      (win) =>
        win.desktopId === desktopId &&
        win.resizable &&
        win.state !== 'minimized' &&
        win.snap?.groupId !== groupId,
    )
    .sort((a, b) => b.z - a.z);
}

export function SnapAssist() {
  const kernel = useKernel();
  const windows = useWindows();
  const desktopId = useActiveDesktopId();
  const [offer, setOffer] = useState<Offer | null>(null);
  /**
   * Which window was in which zone last time round, so a *new* snap can be told from a re-render.
   * Keyed by `id:zoneId`, not by id: moving a window from one zone to another leaves a new hole
   * behind it and deserves the same offer.
   */
  const wasSnapped = useRef<ReadonlySet<string>>(new Set());
  const panel = useRef<HTMLDivElement | null>(null);

  const dismiss = useCallback(() => setOffer(null), []);

  /** The next zone worth offering for `groupId`, or null when there is nothing left to fill. */
  const nextOffer = useCallback(
    (groupId: string, layoutId: string, live: readonly Win[]): Offer | null => {
      const area = kernel.workArea();
      const layout = kernel.policy()
        .snapZones(area)
        .find((candidate) => candidate.id === layoutId);
      if (!layout || layout.zones.length < 2) return null;
      const empty = emptyZones(layout, live, groupId)[0];
      if (!empty) return null;
      if (assistCandidates(live, desktopId, groupId).length === 0) return null;
      return { groupId, layoutId, zoneId: empty.id, rect: zoneRect(empty.rect, area) };
    },
    [kernel, desktopId],
  );

  // The trigger is a window *becoming* snapped, not the existence of a snapped window.
  useEffect(() => {
    const key = (win: Win): string => `${win.id}:${win.snap?.zoneId ?? ''}`;
    const snapped = new Set(windows.filter((win) => win.snap).map(key));
    const previous = wasSnapped.current;
    wasSnapped.current = snapped;
    if (!kernel.policy().snapAssist()) return;

    const fresh = windows.find((win) => win.snap && !previous.has(key(win)));
    if (!fresh?.snap) return;
    const { groupId } = fresh.snap;
    setOffer(nextOffer(groupId, groupId.slice(groupId.indexOf(':') + 1), windows));
  }, [windows, kernel, nextOffer]);

  useEffect(() => {
    if (!offer) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      dismiss();
    };
    const onPointer = (event: PointerEvent): void => {
      if (!panel.current?.contains(event.target as Node)) dismiss();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
    };
  }, [offer, dismiss]);

  if (!offer) return null;

  const candidates = assistCandidates(windows, desktopId, offer.groupId);
  if (candidates.length === 0) return null;

  const choose = (win: Win): void => {
    kernel.snap.snapTo(win.id, offer.layoutId, offer.zoneId);
    // …and on to the next empty zone, so a 2×2 fills itself in three clicks.
    const after = windows.map((candidate) =>
      candidate.id === win.id
        ? ({ ...candidate, snap: { zoneId: offer.zoneId, groupId: offer.groupId } } as Win)
        : candidate,
    );
    setOffer(nextOffer(offer.groupId, offer.layoutId, after));
  };

  return (
    <div
      ref={panel}
      className="win11-snap-assist"
      data-testid="snap-assist"
      data-zone={offer.zoneId}
      role="dialog"
      aria-label={COPY.chrome.snapAssist}
      style={{
        left: `${offer.rect.x}px`,
        top: `${offer.rect.y}px`,
        width: `${offer.rect.w}px`,
        height: `${offer.rect.h}px`,
      }}
    >
      <div className="win11-snap-assist-grid">
        {candidates.map((win) => (
          <button
            key={win.id}
            type="button"
            className="win11-snap-assist-tile"
            data-testid={`snap-assist-${win.id}`}
            data-app-id={win.appId}
            onClick={() => choose(win)}
          >
            <span
              className="win11-snap-assist-icon"
              aria-hidden
              dangerouslySetInnerHTML={{ __html: kernel.windowIconSvg(win) }}
            />
            <span className="win11-snap-assist-label">{win.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
